import { Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormHelperText, FormLabel, IconButton, Input, InputBase, ListItemText, makeStyles, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from "clsx";
import { createRef, forwardRef, Fragment, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { aircraftGroupSelectedIdState, aircraftGroupListFilterWordState, useAircraftGroupList, aircraftGroupExternalListFilterWordState, aircraftGroupExternalSelectedIdState, AircraftDependencyType } from "../../store/AircraftSettingState";
import { userState } from "../../store/UserState";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { AircraftSettingMenu } from "./AircraftSetting";
import RoleControl from "../RoleControl";
import { SUBJ_AIRCRAFT_GROUP_SETTING_LIST, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
			// fontWeight: 'bold',
			whiteSpace: 'nowrap',
		},
		verticalAlign: 'baseline',
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	alignRight: {
		textAlign: 'right',
	},
	containerBox: {
		flex: 1,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		}
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: 2,
	},
	dragImageBox: {
		border: '3px dashed rgba(128, 128, 128, 0.5)',
		borderRadius: '2px',
		'& *': {
			cursor: 'pointer',
		}
	},
	dragImageBoxLabel: {
		fontSize: '75%',
		margin: '-6px 0 2px 14px',
	},
	dragImageBoxActive: {
		borderColor: 'rgba(80, 80, 255, 0.8)',
	},
	dragImageTextBox: {
		width: '210px',
		height: '140px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: '10px',
		fontSize: '15px',
		whiteSpace: 'break-spaces',
	},
	commission: {
		backgroundColor: '#d9ead3ff',
	},
	agreement: {
		backgroundColor: '#f4ccccff',
	},
	sharedCell: {
		height: 0,
	},
	newline: {
		whiteSpace: "break-spaces",
	},
});

const columns = [
	{ id: 'aircraftgroup_name', label: 'Aircraft Group' },
	{ id: 'owner', label: 'Owner' },
	{ id: 'remarks', label: 'Remarks' },
];

const columnsExternal = [
	{ id: 'aircraftgroup_name', label: 'Aircraft Group' },
	{ id: 'owner', label: 'Owner' },
	{ id: 'shared_customer', label: 'Shared with' },
];

const formInputList = [
	{
		code: 'aircraftgroup_name',
		name: 'Aircraft Group',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>? 　]{1,20}$/,
			maxLength: 20,
			unique: true,
		},
		default: ""
	},
	{
		code: 'remarks',
		name: 'Remarks',
		type: 'text',
		rules: {
			required: false,
			pattern: /^[^'"!@#$%^&*();:{}[\]>?]{1,160}$/,
			maxLength: 160,
		},
	},
];

const formDefault = formInputList.reduce((a, v) => {a[v.code] = (v.default || ""); return a}, {});

const AircraftGroupExternalListTableRow = (props) => {
	const user = useRecoilValue(userState);
	const { row, handleClickAircraftGroupExternal } = props;
	const [open, setOpen] = useState(false);

	const columns = columnsExternal;

	const classes = useStyles();
	const {t} = useTranslation();

	return (
		<>
			<TableRow hover role="checkbox" tabIndex={-1}>
				<TableCell key='editBtn' className={classes.tableCell} padding="none" align="center">
					-
				</TableCell>
				{columns.map((column, i) => {
					const value = row[column.id];
					return (
						<TableCell
							key={column.id}
							align={column.align}
							className={clsx(classes.tableCell, row.aircraftgroup_type === 1 && classes.commission, row.aircraftgroup_type === 2 && classes.agreement)}
						>
							{
								column.id === 'aircraftgroup_name' ?
									<OnClickAnchor onClick={() => handleClickAircraftGroupExternal(row.aircraftgroup_id)}>{value}</OnClickAnchor> :
								column.id === 'owner' ?
									(row.shared_customer.find(element => element.customer_code === row.customer_code) || {}).customer_name :
								column.id === 'shared_customer' ?
									<dl>
										<dt>
											<IconButton aria-label="expand row" size="small" style={{ padding: 0, }} onClick={() => setOpen(!open)}>
												{open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
											</IconButton>
											{t(column.label)}
										</dt>
										<dd style={{ marginLeft: 40 }}>
											<Collapse in={open} timeout="auto" unmountOnExit>
												{Array.from(row?.shared_customer).sort((a, b) => {
													if (a.customer_code === user.customer) {
														return -1;
													}
													if (b.customer_code === user.customer) {
														return 1;
													}
													return a.customer_name.localeCompare(b.customer_name);
												}).map(v => (
													<div key={`key-${v.customer_code}`}>{v.customer_name}</div>
												))}
											</Collapse>
										</dd>
									</dl> :
								column.format && typeof value === 'number' ? column.format(value) : value
							}
						</TableCell>
					);
				})}
				<TableCell key='deleteBtn' className={classes.tableCell} padding="none" align="center">
					-
				</TableCell>
			</TableRow>
		</>
	);
};

const AircraftGroupExternalListTable = forwardRef(({filterWord, handleEdit, setWaiting, handleClickAircraftGroupExternal, tableRef}, ref) => {
	const {
		aircraftGroupList,
	} = useAircraftGroupList();

	const columns = columnsExternal;

	const filteredAircraftGroupList = useMemo(() => {
		return aircraftGroupList
			.filter(v => [1, 2].includes(v.aircraftgroup_type))
			.sort((a, b) => a.aircraftgroup_type - b.aircraftgroup_type || a.aircraftgroup_name.localeCompare(b.aircraftgroup_name))
		;
	}, [aircraftGroupList]);

	const list = useCallback(() => {
		if (filterWord === "") {
			return filteredAircraftGroupList;
		}
		return filteredAircraftGroupList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					case 'owner':
						return (row.shared_customer.find(element => element.customer_code === row.customer_code) || {customer_name: ""}).customer_name.indexOf(filterWord) !== -1;
					case 'shared_customer':
						return (row[col.id] || []).some(v => v.customer_name.indexOf(filterWord) !== -1);
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id])) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [filteredAircraftGroupList, filterWord, columns]);

	const classes = useStyles();
	const {t} = useTranslation();

	return (
		<>
			<TableContainer className={classes.container} ref={tableRef}>
				<Table aria-label="sticky table" size="small" className={classes.stickyTable} style={{ tableLayout: 'fixed' }}>
					<TableHead>
						<TableRow className={classes.tableRow}>
							<TableCell
								key='edit'
								className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
								padding="none"
							>
							</TableCell>
							{columns.map((column, i) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: 0 }}
									className={clsx(classes.headerCell, classes.tableCell)}
								>
									{t(column.label)}
								</TableCell>
							))}
							<TableCell
								key='delete'
								className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
								padding="none"
							>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{list().map((row) => {
							return (
								<AircraftGroupExternalListTableRow key={row.aircraftgroup_id} row={row} handleClickAircraftGroupExternal={handleClickAircraftGroupExternal} />
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
});

const AircraftGroupListTable = forwardRef(({filterWord, handleEdit, setWaiting, handleClickAircraftGroup, tableRef}, ref) => {
	const {
		aircraftListAll,
		aircraftGroupList,
		registerAircraftGroup,
		updateAircraftGroup,
		checkDependency,
		deleteAircraftGroup,
		refreshAircraftGroupList
	} = useAircraftGroupList();
	const [del, setDel] = useState();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		const data = {
			column_name: 'aircraftgroup_id',
			value: row.aircraftgroup_id,
		};

		setWaiting(true);
		checkDependency(data)
			.then(res => {
				if (res.size === 0) {
					setDel(row);
					setOpen(true);
				} else {
					if (res.has(AircraftDependencyType.LINKAGE_AIRCRAFT_AIRCRAFTGROUP)) {
						setMessageAlert(t('AIRCRAFT_LINKED_WARNING'));
					} else if (res.has(AircraftDependencyType.LINKAGE_USER_GROUP_AIRCRAFTGROUP)) {
						setMessageAlert(t('USERGROUP_LINKED_WARNING'));
					} else if (res.has(AircraftDependencyType.LINKAGE_OTHER)) {
						setMessageAlert(t('AIRCRAFTGROUP_OTHER_LINKED_WARNING'));
					}
					setOpenAlert(true);
				}
			})
			.catch(e => {
				showError(t('DATA_LOAD_ERROR'));
			})
			.finally(() => {
				setWaiting(false);
			});
	}

	const filteredAircraftGroupList = useMemo(() => {
		return aircraftGroupList
			.filter(v => v.aircraftgroup_type === 0)
			.sort((a, b) => a.aircraftgroup_name.localeCompare(b.aircraftgroup_name))
		;
	}, [aircraftGroupList]);

	const list = useCallback(() => {
		if (filterWord === "") {
			return filteredAircraftGroupList;
		}
		return filteredAircraftGroupList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					case 'owner':
						return (row.shared_customer.find(element => element.customer_code === row.customer_code) || {customer_name: ""}).customer_name.indexOf(filterWord) !== -1;
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id])) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [filteredAircraftGroupList, filterWord]);

	useEffect(() => {
		return () => {
			refreshAircraftGroupList();
		}
	}, [refreshAircraftGroupList]);

	useImperativeHandle(ref, () => ({
		aircraftListAll,
		register: (registerData) => {
			setWaiting(true);
			registerAircraftGroup(registerData)
				.then(() => {
					// console.log("register comp");
				})
				.catch(e => {
					console.error("register error", e);
					if (e?.response?.status === 409) {
						showWarning(t('validate_optimistic_lock'));
						refreshAircraftGroupList();
					} else {
						showError(`${t('Register failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		update: (lockObj, updateData, updateImageData) => {
			setWaiting(true);
			updateAircraftGroup(lockObj, updateData, updateImageData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					console.error("update error", e);
					if (e?.response?.status === 428) {
						showWarning(t('validate_optimistic_lock'));
						refreshAircraftGroupList();
					} else {
						showError(`${t('Update failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		delete: (deleteData) => {
			setWaiting(true);
			deleteAircraftGroup(deleteData)
				.then(() => {
					// console.info("delete comp");
				})
				.catch(e => {
					console.error("delete error", e);
					if (e?.response?.status === 428) {
						showWarning(t('validate_optimistic_lock'));
						refreshAircraftGroupList();
					} else if (e?.response?.status === 424) {
						showError(t('AIRCRAFTGROUP_OTHER_LINKED_WARNING'));
					} else {
						showError(`${t('Delete failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return aircraftGroupList.every(v => v[item] !== value);
		}
	}));

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = () =>{
		const _data = Object.assign({}, del);
		setOpen(false);
		ref.current.delete(_data);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = e => {
		if (e.keyCode === 13) {
			handleDeleteOk();
		}
	};

	const [openAlert, setOpenAlert] = useState(false);
	const handleCloseAlert = () => {
		setOpenAlert(false);
	};
	const [messageAlert, setMessageAlert] = useState('');

	let detail = '';
	if (del) {
		detail = [
			"\n",
			`${t('Aircraft Group')}: ${del.aircraftgroup_name}`
		].join('\n');
	}

	return (
		<>
			<TableContainer className={classes.container} ref={tableRef}>
				<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
					<TableHead>
						<TableRow className={classes.tableRow}>
							<TableCell
								key='edit'
								className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
								padding="none"
							>
							</TableCell>
							{columns.map((column, i) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: 0 }}
									className={clsx(classes.headerCell, classes.tableCell)}
								>
									{t(column.label)}
								</TableCell>
							))}
							<TableCell
								key='delete'
								className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
								padding="none"
							>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{list().map((row) => {
							return (
								<TableRow hover role="checkbox" tabIndex={-1} key={row.aircraftgroup_id}>
									<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
										<RoleControl subject={SUBJ_AIRCRAFT_GROUP_SETTING_LIST} verb={VERB_UPDATE}>
											<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
												<Tooltip title="edit" arrow >
													<PencilIcon fontSize="small" />
												</Tooltip>
											</IconButton>
										</RoleControl>
									</TableCell>
									{columns.map((column, i) => {
										const value = row[column.id];
										return (
											<TableCell
												key={column.id}
												align={column.align}
												className={clsx(classes.tableCell, column.id === 'aircraft_number' && classes.alignRight)}
											>
												{
													column.id === 'aircraftgroup_name' ?
														<OnClickAnchor onClick={() => handleClickAircraftGroup(row.aircraftgroup_id)}>{value}</OnClickAnchor> :
													column.id === 'owner' ?
														(row.shared_customer.find(element => element.customer_code === row.customer_code) || {customer_name: ""}).customer_name :
													column.format && typeof value === 'number' ? column.format(value) : value
												}
											</TableCell>
										);
									})}
									<TableCell key='deleteBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
										<RoleControl subject={SUBJ_AIRCRAFT_GROUP_SETTING_LIST} verb={VERB_DELETE}>
											<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
												<Tooltip title="delete" arrow >
													<DeleteIcon fontSize="small" />
												</Tooltip>
											</IconButton>
										</RoleControl>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{t('AIRCRAFT_GROUP_DELETE_CONFIRM', {detail})}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleDeleteOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={openAlert} onClose={handleCloseAlert}>
				<DialogContent>
					<DialogContentText>{messageAlert}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseAlert} color="primary">OK</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

function AircraftGroupList({changeAircraftSettingMenu}) {
	const tableRef = useRef(null);
	const externalTableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const inputFilterExternalRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useRecoilState(aircraftGroupListFilterWordState);
	const [filterWordExternal, setFileterWordExternal] = useRecoilState(aircraftGroupExternalListFilterWordState);
	const setAircraftGroupSelectedId = useSetRecoilState(aircraftGroupSelectedIdState);
	const setAircraftGroupExternalSelectedId = useSetRecoilState(aircraftGroupExternalSelectedIdState);
	const [open, setOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);

	const handleDelete = (item, name) => () => {
		setValue(item, getValues(item).filter(value => value !== name), {shouldDirty: true});
	};

	const inputRefs = formInputList.reduce((a, v) => (a[v.code] = createRef()) && a, {});

	const { control, formState: { errors, isDirty, isValid }, getValues, reset, setValue } = useForm({
		mode: 'onChange',
	});

	const conv = {};

	const classes = useStyles();
	const {t} = useTranslation();

	useEffect(() => {
		return () => {
			setFileterWord('');
			setFileterWordExternal('');
		}
	}, [setFileterWord, setFileterWordExternal]);

	const handleNew = () => {
		setEditMode(false);
		reset(formDefault);
		setFormData({});
		setOpen(true);
	}

	const handleEdit = (row) => {
		setEditMode(true);
		const _formData = Object.assign({}, row);
		Object.keys(formDefault).forEach(key => _formData[key] === null && (_formData[key] = formDefault[key]));
		reset(_formData);
		setFormData(_formData);
		setOpen(true);
	}

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleSearchExternal = () => {
		const word = inputFilterExternalRef.current.value;
		setFileterWordExternal(word);
		externalTableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilterExternal = () => {
		const word = "";
		inputFilterExternalRef.current.value = word;
		setFileterWordExternal(word);
		externalTableRef.current.scrollTop = 0;
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleSave = () => {
		const data = Object.keys(inputRefs).reduce((a, v) => {
			if (inputRefs[v].current.value !== "") {
				a[v] = inputRefs[v].current.value;
			} else {
				a[v] = null;
			}
			return a;
		}, {});

		if (!editMode) {
			childRef.current.register(data);
		} else {
			const diff = Object.keys(data).reduce((previousValue, currentValue) => {
				if (formData[currentValue] !== data[currentValue]) {
					previousValue[currentValue] = data[currentValue];
				}
				return previousValue;
			}, {});

			let _data = null;
			if (Object.keys(diff).length > 0) {
				_data = Object.assign({}, formData, diff);
			}

			const lockObj = {
				aircraft_code: formData.aircraft_code,
				update_time: formData.update_time,
				update_by_user: formData.update_by_user,
			};

			childRef.current.update(lockObj, _data);
		}
		setOpen(false);
	}

	const handleClickAircraftGroup = useCallback(agid => {
		if (agid) {
			setAircraftGroupSelectedId(agid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT_LINKAGE);
	}, [changeAircraftSettingMenu, setAircraftGroupSelectedId]);

	const handleClickAircraftGroupExternal = useCallback(agid => {
		if (agid) {
			setAircraftGroupExternalSelectedId(agid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT_LINKAGE_EXTERNAL);
	}, [changeAircraftSettingMenu, setAircraftGroupExternalSelectedId]);

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box className={classes.containerBox}>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
					<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(event); event.preventDefault(); }}>
						<div className={classes.searchIcon}>
							<SearchIcon />
						</div>
						<InputBase
							className={classes.input}
							placeholder={`${t('Aircraft Group')}、${t('Owner')}、${t('Remarks')}`}
							inputProps={{ 'aria-label': 'filter aircraft group list' }}
							inputRef={inputFilterRef}
							defaultValue={filterWord}
							// onChange={handleChange}
						/>
						{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
							<CloseIcon />
						</IconButton>}
					</Paper>
					<RoleControl subject={SUBJ_AIRCRAFT_GROUP_SETTING_LIST} verb={VERB_CREATE}>
						<Button variant="outlined" size="small" className={classes.btn} onClick={handleNew}>{t('New')}</Button>
					</RoleControl>
				</Box>
				<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
					{waiting && <Loading backdrop={true} />}
					<Suspense fallback={<Loading />}>
						<AircraftGroupListTable filterWord={filterWord} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} handleClickAircraftGroup={handleClickAircraftGroup} tableRef={tableRef} />
					</Suspense>
				</Box>
			</Box>
			<Box className={classes.containerBox}>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
					<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearchExternal(); event.preventDefault(); }}>
						<div className={classes.searchIcon}>
							<SearchIcon />
						</div>
						<InputBase
							className={classes.input}
							placeholder={`${t('Aircraft Group')}、${t('Owner')}、${t('Shared with')}`}
							inputProps={{ 'aria-label': 'filter external aircraft group list' }}
							inputRef={inputFilterExternalRef}
							defaultValue={filterWordExternal}
							// onChange={handleChange}
						/>
						{filterWordExternal.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilterExternal}>
							<CloseIcon />
						</IconButton>}
					</Paper>
				</Box>
				<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
					{waiting && <Loading backdrop={true} />}
					<Suspense fallback={<Loading />}>
						<AircraftGroupExternalListTable filterWord={filterWordExternal} handleClickAircraftGroupExternal={handleClickAircraftGroupExternal} tableRef={externalTableRef}/>
					</Suspense>
				</Box>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							{formInputList.map(item => (
								item.type === 'text' ?
									<Controller
										key={item.code}
										control={control}
										rules={{
											required: item.rules.required ? t('validate_required') : false,
											pattern: {
												value: item.rules.pattern,
												message: t('validate_pattern')
											},
											maxLength: {
												value: item.rules.maxLength,
												message: t('validate_max_length', { maxLength: item.rules.maxLength }),
											},
											validate: value => {
												if (!item.rules.unique) {
													return true;
												}
												if (formData[item.code] === value) {
													return true;
												}
												return childRef.current.check(item.code, value) || t('already exists')
											}
										}}
										render={({ field: { name, onChange, onBlur, value } }) => (
											<TextField
												className={classes.formInputText}
												label={<div>{item.name}{item.rules.required && <span className="required"> * {t('required')}</span>}</div>}
												name={name}
												id={name}
												variant="outlined"
												size="small"
												InputLabelProps={{
													shrink: true,
												}}
												error={!!errors[name]}
												inputRef={inputRefs[name]}
												// defaultValue={formData[name]}
												helperText={t(errors[name]?.message)}
												onChange={onChange}
												onBlur={onBlur}
												value={value}
												disabled={editMode && item.rules.reedit === false}
											/>
										)}
										name={item.code}
									/>
								: item.type === 'select' ?
									<FormControl
										key={item.code}
										className={classes.formInputText}
									>
										<FormLabel className={classes.dragImageBoxLabel}>{item.name}</FormLabel>
										<Box style={{ minWidth: '473px' }}>
										<Controller
											name={item.code}
											control={control}
											render={({ field: { name, onChange, onBlur, value } }) => (
												<>
													<Select
														labelId={`${item.code}-mutiple-checkbox-label`}
														id={`${name}-mutiple-checkbox`}
														style={{ width: '100%' }}
														multiple
														value={value}
														inputRef={inputRefs[name]}
														onChange={onChange}
														onBlur={onBlur}
														input={<Input id={`${name}-select-multiple-chip`} />}
														renderValue={(selected) => (
															<div className={classes.chips}>
																{selected.map((value) => (
																	<Chip
																		variant="outlined"
																		size="small"
																		key={value}
																		label={(conv[item.code] && conv[item.code][value]) || value}
																		onMouseDown={(event) => {event.stopPropagation();}}
																		onDelete={handleDelete(name, value)}
																		className={classes.chip}
																	/>
																))}
															</div>
														)}
														MenuProps={MenuProps}
													>
														{childRef.current[item.data.list] && childRef.current[item.data.list].map((v) => (
															<MenuItem dense key={v[item.data.key]} value={v[item.data.key]} className={classes.menuItem}>
																<Checkbox checked={value.indexOf(v[item.data.key]) > -1} className={classes.menuItemCheckbox} />
																<ListItemText primary={v[item.data.name]} className={classes.menuItemText} />
															</MenuItem>
														))}
													</Select>
													<FormHelperText className={classes.alignRight}>{value.length}{item.unit}</FormHelperText>
												</>
											)}
											/>
										</Box>
									</FormControl>
								: <></>
							))}
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default AircraftGroupList;
